/**
 * The uncapped cheques, one row each.
 *
 * This was three lines per cheque, wrapping, so a page of twenty-five ran
 * to about two and a half thousand pixels — the reader met the chart, then
 * scrolled a screen and a half of prose-shaped rows to reach the next page
 * of them. A cheque is an amount, a filer, a recipient and a date, and none
 * of that needs three lines.
 *
 * The recipient is what made it wrap. It is free text on the form, and one
 * filer used it to write a paragraph: AARP's runs three sentences and ends
 * "Multiple MOCs on Board of Regents", which is a disclosure in itself and
 * cannot be thrown away to save a line. So the cell is one line with the
 * whole string in its title, and the sworn filing stays one click away.
 *
 * A table rather than a list, with headers, because the section further
 * down this page shipped as an unlabelled wall of figures and read as one.
 */

import { Link } from "react-router-dom";
import { OTHER_KINDS, type OtherGift } from "../api";
import { exact } from "./party";

export function OtherGifts({ gifts }: { gifts: OtherGift[] }) {
  return (
    <div className="mt-5 overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
      <table className="w-full table-fixed text-left">
        <thead className="border-b border-line bg-sunk text-xs uppercase tracking-wide text-faint">
          <tr>
            <th scope="col" className="w-32 px-3 py-2 text-right font-medium">
              Amount
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Who gave it
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Who received it
            </th>
            <th scope="col" className="hidden w-44 px-3 py-2 font-medium sm:table-cell">
              What for
            </th>
            <th scope="col" className="hidden w-28 whitespace-nowrap px-3 py-2 font-medium sm:table-cell">
              When
            </th>
            <th scope="col" className="w-24 px-3 py-2 font-medium">
              <span className="sr-only">The filing</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {gifts.map((gift, index) => (
            <tr
              key={`${gift.registrant}-${gift.honoree}-${gift.amount}-${index}`}
              className="align-baseline hover:bg-sunk/60"
            >
              <td className="px-3 py-2 text-right font-medium tabular-nums">
                {/* Exact, not shortened. The cards above are the summary;
                    this row is the evidence, and a reader checking it
                    against the filing needs the figure the filing carries. */}
                {exact(gift.amount)}
              </td>
              <td className="truncate px-3 py-2" title={gift.registrant}>
                {gift.registrant_id ? (
                  <Link to={`/money/${gift.registrant_id}`} className="hover:text-brand">
                    {gift.registrant}
                  </Link>
                ) : (
                  gift.registrant
                )}
              </td>
              {/* The whole string is in the title, including the sentences
                  a filer appended to a name. See the note above. */}
              <td className="truncate px-3 py-2 text-muted" title={gift.honoree}>
                {gift.honoree}
              </td>
              <td className="hidden whitespace-nowrap px-3 py-2 text-sm text-faint sm:table-cell">
                {OTHER_KINDS[gift.kind].label}
              </td>
              <td className="hidden whitespace-nowrap px-3 py-2 text-sm tabular-nums text-faint sm:table-cell">
                {gift.date ?? gift.filing_year}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm">
                {gift.source && (
                  <a
                    href={gift.source}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand hover:underline"
                  >
                    The filing
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
